import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy, HostListener } from '@angular/core';
import {
  IonApp, IonSplitPane, IonMenu, IonContent, IonList, IonItem, IonIcon,
  IonLabel, IonRouterOutlet, IonMenuToggle, IonToggle, IonFooter, IonToolbar, IonSelect, IonSelectOption,
} from '@ionic/angular';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { addIcons } from 'ionicons';
import {
  gridOutline, peopleOutline, storefrontOutline, beerOutline, fileTrayFullOutline,
  mapOutline, busOutline, cartOutline, checkboxOutline, bicycleOutline, cashOutline,
  pricetagsOutline, logOutOutline, wineOutline, downloadOutline, albumsOutline,
  moonOutline, clipboardOutline, settingsOutline, businessOutline, receiptOutline, calendarOutline,
  chevronBackOutline, chevronForwardOutline, chevronDownOutline, chevronUpOutline, sunnyOutline, statsChartOutline,
  shieldCheckmarkOutline,
} from 'ionicons/icons';
import { AlertController } from '@ionic/angular';
import { AuthService } from './core/auth/auth.service';
import { ThemeService } from './core/theme.service';
import { ActualizacionesService } from './core/actualizaciones.service';
import { instalarAvisoCierre } from './shared/aviso-cierre';
import { Rol } from './core/models';

interface MenuEntry {
  titulo: string;
  url: string;
  icono: string;
  roles?: Rol[];
}

interface MenuSeccion {
  titulo: string;
  entradas: MenuEntry[];
}

// El menú va por flujos de trabajo, no por departamentos: Disylog lleva dos
// negocios distintos (vende su propio catálogo y distribuye la mercancía de
// CLUN/Clesa/Feiraco con hojas de carga) y antes las dos cosas se mezclaban en
// «Ventas» junto con la logística. Cada rol ve solo las secciones con las que
// trabaja; el orden dentro de cada una sigue el ciclo real del trabajo.
const SECCIONES: MenuSeccion[] = [
  {
    titulo: 'Inicio',
    entradas: [
      { titulo: 'Dashboard', url: '/dashboard', icono: 'grid-outline', roles: ['gerente', 'director_comercial', 'comercial'] },
      { titulo: 'Informe diario', url: '/informe-diario', icono: 'stats-chart-outline', roles: ['gerente', 'director_comercial', 'comercial'] },
      { titulo: 'Calendario de entregas', url: '/calendario', icono: 'calendar-outline', roles: ['gerente', 'director_comercial', 'almacen'] },
    ],
  },
  {
    titulo: 'Ventas',
    entradas: [
      { titulo: 'Pedidos de venta', url: '/pedidos', icono: 'cart-outline', roles: ['gerente', 'director_comercial', 'comercial', 'almacen'] },
      { titulo: 'Clientes', url: '/clientes', icono: 'people-outline', roles: ['gerente', 'director_comercial', 'comercial', 'almacen'] },
      { titulo: 'Tarifas y promociones', url: '/tarifas', icono: 'pricetags-outline', roles: ['gerente', 'director_comercial'] },
      { titulo: 'Facturación', url: '/facturas', icono: 'cash-outline', roles: ['gerente', 'director_comercial'] },
    ],
  },
  {
    // Mercancía que se reparte por cuenta de CLUN, Clesa y Feiraco: no factura
    // ni mueve stock, tiene su propio papel (la hoja de carga) y su propio
    // fichero de exportación. Es un negocio aparte y se ve como tal.
    titulo: 'Distribución',
    entradas: [
      { titulo: 'Hojas de carga', url: '/hojas-carga', icono: 'clipboard-outline', roles: ['gerente', 'director_comercial', 'almacen'] },
      { titulo: 'Suministradoras', url: '/suministradoras', icono: 'business-outline', roles: ['gerente', 'director_comercial'] },
    ],
  },
  {
    titulo: 'Almacén y reparto',
    entradas: [
      { titulo: 'Picking', url: '/picking', icono: 'checkbox-outline', roles: ['gerente', 'almacen', 'director_comercial'] },
      { titulo: 'Repartos', url: '/repartos', icono: 'bicycle-outline', roles: ['gerente', 'repartidor', 'director_comercial'] },
      { titulo: 'Rutas', url: '/rutas', icono: 'map-outline', roles: ['gerente', 'director_comercial', 'comercial', 'almacen'] },
      { titulo: 'Flota y transportistas', url: '/flota', icono: 'bus-outline', roles: ['gerente', 'director_comercial'] },
      { titulo: 'Stock e inventario', url: '/almacenes', icono: 'file-tray-full-outline', roles: ['gerente', 'almacen', 'director_comercial'] },
    ],
  },
  {
    titulo: 'Compras',
    entradas: [
      { titulo: 'Pedidos a proveedor', url: '/pedidos-compra', icono: 'receipt-outline', roles: ['gerente', 'almacen', 'director_comercial'] },
      { titulo: 'Recepción', url: '/recepcion', icono: 'download-outline', roles: ['gerente', 'almacen', 'director_comercial'] },
      { titulo: 'Proveedores', url: '/proveedores', icono: 'storefront-outline', roles: ['gerente', 'almacen', 'director_comercial'] },
    ],
  },
  {
    titulo: 'Catálogo',
    entradas: [
      { titulo: 'Artículos', url: '/articulos', icono: 'beer-outline', roles: ['gerente', 'director_comercial', 'comercial', 'almacen'] },
    ],
  },
];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [IonSelect, IonSelectOption, FormsModule, 
    IonApp, IonSplitPane, IonMenu, IonItem, IonIcon,
    IonRouterOutlet, IonMenuToggle, IonToggle,
    RouterLink, RouterLinkActive,
  ],
  template: `
    <ion-app>
      <ion-split-pane contentId="main" [when]="auth.autenticado() ? 'lg' : false" [class.colapsado]="menuColapsado()">
        <ion-menu contentId="main" type="overlay" [disabled]="!auth.autenticado()" [class.colapsado]="menuColapsado()">
          <div class="lateral">
            <div class="marca" [class.colapsada]="menuColapsado()">
              <div class="marca-icono"><ion-icon name="wine-outline"></ion-icon></div>
              @if (!menuColapsado()) {
                <div class="marca-texto">
                  <strong>Bebidas</strong>
                  <span>{{ auth.usuario()?.company_name }}</span>
                </div>
              }
              <button type="button" class="colapsar solo-escritorio" (click)="alternarMenu()"
                      [attr.aria-label]="menuColapsado() ? 'Expandir menú' : 'Contraer menú'" [title]="menuColapsado() ? 'Expandir menú' : 'Contraer menú'">
                <ion-icon [name]="menuColapsado() ? 'chevron-forward-outline' : 'chevron-back-outline'"></ion-icon>
              </button>
            </div>

            @if (auth.rol() === 'admin' && empresas().length > 1 && !menuColapsado()) {
              <ion-item lines="none" class="empresa">
                <ion-select label="Empresa" interface="popover" [ngModel]="empresaActiva()" (ngModelChange)="cambiarEmpresa($event)">
                  @for (c of empresas(); track c.id) { <ion-select-option [value]="c.id">{{ c.name }}</ion-select-option> }
                </ion-select>
              </ion-item>
            }

            <nav class="secciones" aria-label="Menú principal">
              @for (seccion of secciones(); track seccion.titulo) {
                <div class="seccion" [class.cerrada]="!abierta(seccion.titulo) && !menuColapsado()">
                  @if (!menuColapsado()) {
                    <button type="button" class="seccion-cab" (click)="alternarSeccion(seccion.titulo)" [attr.aria-expanded]="abierta(seccion.titulo)">
                      <span>{{ seccion.titulo }}</span>
                      <ion-icon name="chevron-down-outline" class="chev" [class.girado]="!abierta(seccion.titulo)"></ion-icon>
                    </button>
                  } @else {
                    <div class="seccion-sep" [title]="seccion.titulo"></div>
                  }
                  <div class="entradas">
                    @for (entry of seccion.entradas; track entry.url) {
                      <ion-menu-toggle auto-hide="false">
                        <a class="entrada" [routerLink]="entry.url" routerLinkActive="activa" [routerLinkActiveOptions]="{ exact: false }"
                           [title]="entry.titulo" [class.colapsada]="menuColapsado()">
                          <ion-icon [name]="entry.icono"></ion-icon>
                          @if (!menuColapsado()) { <span>{{ entry.titulo }}</span> }
                        </a>
                      </ion-menu-toggle>
                    }
                  </div>
                </div>
              }
            </nav>

            <div class="pie">
              @if (menuUsuarioAbierto() && !menuColapsado()) {
                <div class="menu-usuario" role="menu">
                  @if (auth.tieneRol('gerente')) {
                    <ion-menu-toggle auto-hide="false">
                      <a class="entrada" routerLink="/ajustes" routerLinkActive="activa" (click)="menuUsuarioAbierto.set(false)">
                        <ion-icon name="settings-outline"></ion-icon><span>Configuración</span>
                      </a>
                    </ion-menu-toggle>
                  }
                  <ion-menu-toggle auto-hide="false">
                    <a class="entrada" routerLink="/verifactu/declaracion-responsable" routerLinkActive="activa" (click)="menuUsuarioAbierto.set(false)">
                      <ion-icon name="shield-checkmark-outline"></ion-icon><span>Declaración VERI*FACTU</span>
                    </a>
                  </ion-menu-toggle>
                  <label class="entrada tema">
                    <ion-icon [name]="theme.oscuro() ? 'moon-outline' : 'sunny-outline'"></ion-icon><span>Modo oscuro</span>
                    <ion-toggle [checked]="theme.oscuro()" (ionChange)="theme.alternar()" aria-label="Modo oscuro"></ion-toggle>
                  </label>
                  <button type="button" class="entrada salir" (click)="confirmarSalir()">
                    <ion-icon name="log-out-outline"></ion-icon><span>Salir</span>
                  </button>
                </div>
              }
              <button type="button" class="avatar" (click)="menuColapsado() ? confirmarSalir() : menuUsuarioAbierto.set(!menuUsuarioAbierto())"
                      [class.colapsado]="menuColapsado()" [attr.aria-expanded]="menuUsuarioAbierto()"
                      [title]="menuColapsado() ? 'Salir' : (auth.usuario()?.name ?? '')">
                <span class="iniciales">{{ iniciales() }}</span>
                @if (!menuColapsado()) {
                  <span class="avatar-texto">
                    <strong>{{ auth.usuario()?.name }}</strong>
                    <small>{{ etiquetaRol(auth.rol()) }}@for (r of auth.usuario()?.roles_extra ?? []; track r) { · {{ etiquetaRol(r) }}}</small>
                  </span>
                  <ion-icon [name]="menuUsuarioAbierto() ? 'chevron-down-outline' : 'chevron-up-outline'" class="chev-avatar"></ion-icon>
                }
              </button>
            </div>
          </div>
        </ion-menu>
        <ion-router-outlet id="main"></ion-router-outlet>
      </ion-split-pane>
    </ion-app>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      /* Sidebar estilo Silvanus: cabecera de marca en color, grupos plegables, avatar con menú al pie
         y modo contraído (solo iconos) en escritorio. */
      ion-menu { --width: 280px; --background: var(--ion-background-color); }
      ion-menu::part(container) { transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
      @media (min-width: 992px) {
        ion-split-pane { --side-width: 280px; --side-max-width: 280px; --side-min-width: 64px; transition: --side-width 0.25s; }
        ion-split-pane.colapsado { --side-width: 64px; --side-max-width: 64px; }
        ion-menu.colapsado { --width: 64px; }
      }
      .solo-escritorio { display: none; }
      @media (min-width: 992px) { .solo-escritorio { display: inline-flex; } }

      .lateral { display: flex; flex-direction: column; height: 100%; padding: 10px 8px; box-sizing: border-box; overflow: hidden; }
      .marca { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 12px; color: #fff; position: relative;
        background: linear-gradient(135deg, var(--ion-color-primary-shade), var(--ion-color-primary-tint)); box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18); }
      .marca.colapsada { justify-content: center; padding: 8px 0; }
      .marca-icono { width: 34px; height: 34px; border-radius: 9px; background: rgba(255, 255, 255, 0.18); display: grid; place-items: center; flex-shrink: 0; }
      .marca-icono ion-icon { font-size: 22px; }
      .marca-texto { min-width: 0; flex: 1; }
      .marca-texto strong { display: block; font-family: var(--font-display); font-size: 1.15rem; line-height: 1.1; }
      .marca-texto span { display: block; font-size: 0.7rem; opacity: 0.85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .colapsar { width: 26px; height: 26px; border-radius: 50%; border: 1px solid rgba(255, 255, 255, 0.4); background: rgba(255, 255, 255, 0.18); color: #fff;
        align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; padding: 0; }
      .marca.colapsada .colapsar { position: absolute; right: -6px; bottom: -8px; width: 22px; height: 22px; background: var(--ion-color-primary); }
      .colapsar:focus-visible { outline: 2px solid #fff; }

      .empresa { margin: 8px 0 0; --background: transparent; --padding-start: 8px; font-size: 0.85rem; }

      .secciones { flex: 1; overflow-y: auto; overflow-x: hidden; margin-top: 10px; padding-right: 2px; }
      .seccion-cab { width: 100%; display: flex; align-items: center; justify-content: space-between; background: none; border: 0; cursor: pointer;
        padding: 10px 10px 4px; font: inherit; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: var(--ion-color-medium); }
      .seccion-cab .chev { font-size: 14px; transition: transform 0.2s; } .seccion-cab .chev.girado { transform: rotate(-90deg); }
      .seccion.cerrada .entradas { display: none; }
      .seccion-sep { height: 1px; margin: 8px 12px; background: var(--ion-color-step-150, rgba(0, 0, 0, 0.1)); }
      .entradas { display: flex; flex-direction: column; gap: 2px; }
      .entrada { display: flex; align-items: center; gap: 12px; padding: 9px 12px; border-radius: 10px; color: var(--ion-text-color); text-decoration: none;
        font-size: 0.93rem; position: relative; border: 0; background: none; width: 100%; font: inherit; text-align: left; cursor: pointer; box-sizing: border-box; }
      .entrada ion-icon { font-size: 20px; color: var(--ion-color-medium); flex-shrink: 0; }
      .entrada:hover { background: rgba(var(--ion-color-primary-rgb), 0.07); }
      .entrada.activa { background: rgba(var(--ion-color-primary-rgb), 0.13); color: var(--ion-color-primary); font-weight: 600; }
      .entrada.activa ion-icon { color: var(--ion-color-primary); }
      .entrada.activa::before { content: ''; position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px; border-radius: 3px; background: var(--ion-color-primary); }
      .entrada.colapsada { justify-content: center; padding: 10px 0; }
      .entrada:focus-visible { outline: 2px solid var(--ion-color-primary); outline-offset: 1px; }

      .pie { margin-top: 8px; border-top: 1px solid var(--ion-color-step-100, rgba(0, 0, 0, 0.08)); padding-top: 8px; }
      .menu-usuario { display: flex; flex-direction: column; gap: 2px; margin-bottom: 6px; padding: 6px; border-radius: 12px;
        background: var(--ion-item-background, var(--ion-background-color)); border: 1px solid var(--ion-color-step-100, rgba(0, 0, 0, 0.08)); }
      .entrada.tema ion-toggle { margin-left: auto; --handle-width: 18px; height: 22px; }
      .entrada.salir { color: var(--ion-color-danger); } .entrada.salir ion-icon { color: var(--ion-color-danger); }
      .avatar { display: flex; align-items: center; gap: 10px; width: 100%; border: 0; background: none; cursor: pointer; padding: 6px 8px; border-radius: 12px;
        font: inherit; color: inherit; text-align: left; }
      .avatar:hover { background: rgba(var(--ion-color-primary-rgb), 0.07); }
      .avatar.colapsado { justify-content: center; padding: 6px 0; }
      .iniciales { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; font-weight: 700; font-size: 0.85rem; flex-shrink: 0;
        background: rgba(var(--ion-color-primary-rgb), 0.15); color: var(--ion-color-primary); }
      .avatar-texto { min-width: 0; flex: 1; display: flex; flex-direction: column; }
      .avatar-texto strong { font-size: 0.9rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .avatar-texto small { font-size: 0.72rem; color: var(--ion-color-medium); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .chev-avatar { color: var(--ion-color-medium); font-size: 16px; }
    `,
  ],
})
export class AppComponent {
  auth = inject(AuthService);
  private http = inject(HttpClient);
  theme = inject(ThemeService);
  private alertas = inject(AlertController);

  empresas = signal<{ id: string; name: string }[]>([]);
  empresaActiva = signal<string | null>(localStorage.getItem('bebidas.company'));

  /** El admin cambia de empresa: se guarda y se recarga para que todo apunte a la nueva. */
  cambiarEmpresa(id: string | null): void {
    if (!id || id === this.empresaActiva()) return;
    localStorage.setItem('bebidas.company', id);
    this.empresaActiva.set(id);
    location.reload();
  }

  private async cargarEmpresas(): Promise<void> {
    try {
      const r = await firstValueFrom(this.http.get<{ companies: { id: string; name: string }[] }>('/companies', { params: { limit: '100' } }));
      this.empresas.set(r.companies);
      if (!this.empresaActiva()) this.empresaActiva.set(this.auth.usuario()?.company_id ?? null);
    } catch {
      // sin permiso o sin red: sin selector
    }
  }

  /** Atajos de escritorio: «n» pulsa el botón + de la pantalla, «/» enfoca su buscador. No actúan escribiendo en un campo. */
  @HostListener('window:keydown', ['$event'])
  atajosTeclado(e: KeyboardEvent): void {
    const objetivo = e.target as HTMLElement | null;
    const escribiendo = !!objetivo?.closest('input, textarea, ion-input, ion-textarea, ion-searchbar, [contenteditable]');
    if (escribiendo || e.ctrlKey || e.metaKey || e.altKey || document.querySelector('ion-modal.show-modal, ion-alert, ion-action-sheet')) return;
    if (e.key === 'n') {
      const fab = document.querySelector<HTMLElement>('ion-fab-button');
      if (fab) { e.preventDefault(); fab.click(); }
    } else if (e.key === '/') {
      const buscador = document.querySelector<HTMLIonSearchbarElement>('ion-content ion-searchbar');
      if (buscador) { e.preventDefault(); void buscador.setFocus(); }
    }
  }

  menuColapsado = signal(localStorage.getItem('bebidas.menu') === 'colapsado');
  menuUsuarioAbierto = signal(false);
  private seccionesCerradas = signal(new Set<string>(JSON.parse(localStorage.getItem('bebidas.menu.cerradas') ?? '[]') as string[]));

  alternarMenu(): void {
    this.menuColapsado.update((v) => !v);
    this.menuUsuarioAbierto.set(false);
    localStorage.setItem('bebidas.menu', this.menuColapsado() ? 'colapsado' : 'abierto');
  }

  abierta(seccion: string): boolean {
    return !this.seccionesCerradas().has(seccion);
  }

  alternarSeccion(seccion: string): void {
    this.seccionesCerradas.update((s) => {
      const n = new Set(s);
      if (n.has(seccion)) n.delete(seccion); else n.add(seccion);
      localStorage.setItem('bebidas.menu.cerradas', JSON.stringify([...n]));
      return n;
    });
  }

  iniciales(): string {
    const n = (this.auth.usuario()?.name ?? '').trim().split(/\s+/).filter(Boolean);
    return ((n[0]?.[0] ?? '') + (n[1]?.[0] ?? '')).toUpperCase() || 'U';
  }

  etiquetaRol(rol: Rol | null | undefined): string {
    const etiquetas: Record<string, string> = {
      gerente: 'Gerente', director_comercial: 'Director comercial', comercial: 'Comercial',
      almacen: 'Almacén', repartidor: 'Repartidor', admin: 'Administrador',
    };
    return etiquetas[rol ?? ''] ?? (rol ?? '');
  }

  /** Cerrar sesión desde el menú overlay en móvil es fácil de tocar sin querer. */
  async confirmarSalir(): Promise<void> {
    const alerta = await this.alertas.create({
      header: 'Cerrar sesión',
      message: '¿Salir de la plataforma?',
      buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Salir', role: 'destructive', handler: () => this.auth.logout() }],
    });
    await alerta.present();
  }

  secciones = computed(() => {
    const rol = this.auth.rol();
    if (!rol) return [];
    return SECCIONES.map((s) => ({
      ...s,
      entradas: s.entradas.filter((e) => !e.roles || this.auth.tieneRol(...e.roles)),
    })).filter((s) => s.entradas.length > 0);
  });

  private readonly actualizaciones = inject(ActualizacionesService);

  constructor() {
    this.actualizaciones.vigilar();
    instalarAvisoCierre(this.alertas);
    effect(() => {
      if (this.auth.autenticado() && this.auth.rol() === 'admin') void this.cargarEmpresas();
    });
    addIcons({
      gridOutline, peopleOutline, storefrontOutline, beerOutline, fileTrayFullOutline,
      mapOutline, busOutline, cartOutline, checkboxOutline, bicycleOutline, cashOutline,
      pricetagsOutline, logOutOutline, wineOutline, downloadOutline, albumsOutline,
      moonOutline, clipboardOutline, settingsOutline, businessOutline, receiptOutline, calendarOutline, chevronBackOutline, chevronForwardOutline, chevronDownOutline, chevronUpOutline, sunnyOutline, statsChartOutline,
      shieldCheckmarkOutline });
  }
}
